import { supabase } from './supabaseClient';

// ─── AUTH ─────────────────────────────────────────────────────────────────────

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function resetPassword(email) {
  // window.location.origin already reflects whichever subdomain the user is
  // on (e.g. aksrealty.txcoordinatorpro.com) — no brokerage lookup needed,
  // this is a same-session browser action.
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin,
  });
  if (error) throw error;
}

export async function updatePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

export function onAuthStateChange(callback) {
  return supabase.auth.onAuthStateChange((_event, session) => {
    callback(session);
  });
}

// ─── USERS ────────────────────────────────────────────────────────────────────

// Map snake_case DB fields to camelCase for the app
export function fromDbUser(d) {
  if (!d) return null;
  return {
    id:          d.id,
    name:        d.name        || '',
    role:        d.role        || 'realtor',
    brokerId:    d.broker_id   || null,
    brokerageId: d.brokerage_id || null,
    licenseNo:   d.license_no  || '',
    email:       d.email       || '',
    phone:       d.phone       || '',
    address:     d.address     || '',
    city:        d.city        || '',
    state:       d.state       || 'AL',
    zip:         d.zip         || '',
    dob:         d.dob         || '',
    photoUrl:    d.photo_url   || null,
    photo:       d.photo_url   || null,   // alias used by Avatar component
    emergency:   d.emergency   || {},
    fees:        d.fees        || { agentSplit:95, transactionFee:0, eoFee:0, customFees:[] },
    banking:     d.banking     || { achDoc:null, w9Doc:null },
    createdAt:   d.created_at,
  };
}

export async function fetchCurrentUser(authId) {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', authId)
    .single();
  if (error) throw error;

  // Get email from auth.users since it's not in public.users
  const { data: authData } = await supabase.auth.getUser();
  const email = authData?.user?.email || '';

  return fromDbUser({ ...data, email });
}

export async function fetchAllUsers() {
  // email now lives directly on public.users (set by handle_new_user at
  // invite time) — no more admin.listUsers() round-trip needed.
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .order('role')
    .order('name');
  if (error) throw error;
  return (data || []).map(fromDbUser);
}

export async function updateUser(id, updates) {
  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) {
    console.error("updateUser() failed:", error);
    throw error;
  }
  return fromDbUser(data);
}

// createAuthUser removed — use inviteUser instead

// ─── PROFILE PHOTO ────────────────────────────────────────────────────────────

export async function uploadAvatar(userId, file) {
  const ext  = file.name.split('.').pop();
  const path = `${userId}/avatar.${ext}`;
  const { error } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  return data.publicUrl;
}

// ─── BANKING DOCS ────────────────────────────────────────────────────────────

export async function uploadBankingDoc(userId, docType, file) {
  const ext  = file.name.split('.').pop();
  const path = `${userId}/${docType}.${ext}`;
  const { error: upErr } = await supabase.storage
    .from('banking')
    .upload(path, file, { upsert: true });
  if (upErr) throw upErr;

  // Upsert metadata into banking_docs table
  const { data, error } = await supabase
    .from('banking_docs')
    .upsert({
      user_id:          userId,
      doc_type:         docType,
      name:             file.name,
      size:             file.size,
      mime_type:        file.type,
      storage_path:     path,
    }, { onConflict: 'user_id,doc_type' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getBankingDocUrl(storagePath) {
  const { data, error } = await supabase.storage
    .from('banking')
    .createSignedUrl(storagePath, 3600); // 1 hour
  if (error) throw error;
  return data.signedUrl;
}

export async function deleteBankingDoc(userId, docType, storagePath) {
  await supabase.storage.from('banking').remove([storagePath]);
  await supabase.from('banking_docs').delete()
    .eq('user_id', userId).eq('doc_type', docType);
}

export async function fetchBankingDocs(userId) {
  const { data, error } = await supabase
    .from('banking_docs')
    .select('*')
    .eq('user_id', userId);
  if (error) throw error;
  return data || [];
}

// ─── TRANSACTIONS ─────────────────────────────────────────────────────────────

export async function fetchTransactions(userId, userRole) {
  const client = supabase; // RLS enforces access — see supabase/migrations
  const { data, error } = await client
    .from('transactions')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const mapped = (data || []).map(fromDbTransaction);
  return mapped;
}

export async function createTransaction(tx) {
  const client = supabase; // RLS enforces access — see supabase/migrations

  const { data, error } = await client
    .from('transactions')
    .insert(toDbTransaction(tx))
    .select()
    .single();
  if (error) throw error;
  return fromDbTransaction(data);
}

export async function updateTransaction(id, updates) {
  const client = supabase; // RLS enforces access — see supabase/migrations
  const { data, error } = await client
    .from('transactions')
    .update(toDbTransaction(updates))
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return fromDbTransaction(data);
}

export async function deleteTransaction(id) {
  const client = supabase; // RLS enforces access — see supabase/migrations
  const { error } = await client.from('transactions').delete().eq('id', id);
  if (error) throw error;
}

// camelCase ↔ snake_case mapping
function toDbTransaction(tx) {
  const d = {};
  if (tx.agentId        !== undefined) d.agent_id            = tx.agentId;
  if (tx.brokerageId    !== undefined) d.brokerage_id        = tx.brokerageId;
  if (tx.side           !== undefined) d.side                = tx.side;
  if (tx.address        !== undefined) d.address             = tx.address;
  if (tx.clientName     !== undefined) d.client_name         = tx.clientName;
  if (tx.price          !== undefined) d.price               = tx.price;
  if (tx.stage          !== undefined) d.stage               = tx.stage;
  if (tx.wfState        !== undefined) d.wf_state            = tx.wfState;
  if (tx.commissionPct  !== undefined) d.commission_pct      = tx.commissionPct;
  if (tx.referralFee    !== undefined) d.referral_fee        = tx.referralFee;
  if (tx.agentSplit     !== undefined) d.agent_split         = tx.agentSplit;
  if (tx.transactionFee !== undefined) d.transaction_fee     = tx.transactionFee;
  if (tx.eoFee          !== undefined) d.eo_fee              = tx.eoFee;
  if (tx.customFees     !== undefined) d.custom_fees         = tx.customFees;
  if (tx.contractDate   !== undefined) d.contract_date       = tx.contractDate || null;
  if (tx.inspectionDeadline !== undefined) d.inspection_deadline = tx.inspectionDeadline || null;
  if (tx.appraisalDeadline  !== undefined) d.appraisal_deadline  = tx.appraisalDeadline  || null;
  if (tx.closingDate    !== undefined) d.closing_date        = tx.closingDate   || null;
  if (tx.submittedAt    !== undefined) d.submitted_at        = tx.submittedAt;
  if (tx.notes          !== undefined) d.notes               = tx.notes;
  if (tx.parties        !== undefined) d.parties             = tx.parties;
  if (tx.contacts       !== undefined) d.contacts            = tx.contacts;
  if (tx.reviewLog      !== undefined) d.review_log          = tx.reviewLog;
  return d;
}

export function fromDbTransaction(d) {
  // Use null coalescing carefully — 0 is a valid value for fees
  const num = (v, def=0) => (v !== null && v !== undefined) ? Number(v) : def;
  return {
    id:                  d.id,
    agentId:             d.agent_id,
    brokerageId:         d.brokerage_id,
    side:                d.side                 || 'Buyer',
    address:             d.address              || '',
    clientName:          d.client_name          || '',
    price:               num(d.price,            0),
    stage:               d.stage                || 'Pre-Listing / Pre-Offer',
    wfState:             d.wf_state             || 'draft',
    commissionPct:       num(d.commission_pct,   2.5),
    referralFee:         num(d.referral_fee,     0),
    agentSplit:          num(d.agent_split,      95),
    transactionFee:      num(d.transaction_fee,  0),
    eoFee:               num(d.eo_fee,           0),
    customFees:          d.custom_fees           || [],
    contractDate:        d.contract_date         || '',
    inspectionDeadline:  d.inspection_deadline   || '',
    appraisalDeadline:   d.appraisal_deadline    || '',
    closingDate:         d.closing_date          || '',
    submittedAt:         d.submitted_at          || null,
    notes:               d.notes                || '',
    parties:             d.parties              || [],
    contacts:            d.contacts             || [],
    reviewLog:           d.review_log           || [],
    createdAt:           d.created_at,
    tasks:               [], // loaded separately
  };
}

// ─── TASKS ────────────────────────────────────────────────────────────────────

export async function fetchTasksForTransaction(transactionId, resolveUrls = false) {
  const client = supabase; // RLS enforces access — see supabase/migrations
  const { data, error } = await client
    .from('tasks')
    .select('*, documents(*)')
    .eq('transaction_id', transactionId)
    .order('sort_order');
  if (error) throw error;

  const tasks = (data || []).map(fromDbTask);

  // Only resolve signed URLs when explicitly requested (e.g. opening the checklist)
  // Skip on initial page load to avoid 100+ API calls across all transactions
  if (resolveUrls) {
    await Promise.all(
      tasks.flatMap(task =>
        task.documents.map(async doc => {
          if (!doc.storagePath) return;
          try {
            doc.dataUrl = await getDocumentUrl(doc.storagePath);
          } catch (e) {
            console.warn("Could not resolve signed URL:", doc.name, e.message);
          }
        })
      )
    );
  }

  return tasks;
}

export async function createTask(transactionId, task, sortOrder) {
  const { data, error } = await supabase
    .from('tasks')
    .insert({
      transaction_id: transactionId,
      template_id:    task.templateId || null,
      title:          task.title,
      mandatory:      task.mandatory,
      side:           task.side,
      category:       task.category,
      description:    task.description,
      completed:      task.completed || false,
      due_date:       task.dueDate || null,
      notes:          task.notes || '',
      sort_order:     sortOrder || 0,
    })
    .select()
    .single();
  if (error) throw error;
  return fromDbTask(data);
}

export async function updateTask(id, updates) {
  const client = supabase; // RLS enforces access — see supabase/migrations
  const d = {};
  if (updates.completed !== undefined) d.completed = updates.completed;
  if (updates.dueDate   !== undefined) d.due_date  = updates.dueDate || null;
  if (updates.notes     !== undefined) d.notes     = updates.notes;
  const { data, error } = await client
    .from('tasks').update(d).eq('id', id).select().single();
  if (error) {
    console.error("updateTask failed for id:", id, error);
    throw error;
  }
  return fromDbTask(data);
}

export async function bulkCreateTasks(transactionId, tasks, sortOffset = 0) {
  const client = supabase; // RLS enforces access — see supabase/migrations
  const rows = tasks.map((t, i) => ({
    transaction_id: transactionId,
    template_id:    t.templateId || null,
    title:          t.title,
    mandatory:      t.mandatory,
    side:           t.side,
    category:       t.category,
    description:    t.description,
    completed:      false,
    sort_order:     sortOffset + i,
  }));
  const { data, error } = await client.from('tasks').insert(rows).select();
  if (error) throw error;
  return (data || []).map(fromDbTask);
}

function fromDbTask(d) {
  return {
    id:           d.id,
    templateId:   d.template_id,
    title:        d.title,
    mandatory:    d.mandatory,
    side:         d.side,
    category:     d.category,
    description:  d.description || '',
    completed:    d.completed,
    dueDate:      d.due_date    || '',
    notes:        d.notes       || '',
    documents:    (d.documents || []).map(fromDbDocument),
  };
}

// ─── DOCUMENTS ────────────────────────────────────────────────────────────────

export async function uploadDocument(taskId, transactionId, file, uploadedBy, uploadedByName) {
  const client = supabase; // RLS enforces access — see supabase/migrations
  const path = `${transactionId}/${taskId}/${Date.now()}_${file.name}`;

  // Upload file to storage
  const { error: upErr } = await client.storage
    .from('documents')
    .upload(path, file, { upsert: false });
  if (upErr) { console.error("Document storage upload failed:", upErr); throw upErr; }

  // Insert metadata
  const { data, error } = await client
    .from('documents')
    .insert({
      task_id:          taskId,
      transaction_id:   transactionId,
      name:             file.name,
      size:             file.size,
      mime_type:        file.type,
      storage_path:     path,
      uploaded_by:      uploadedBy,
      uploaded_by_name: uploadedByName,
    })
    .select()
    .single();
  if (error) {
    console.error("Document metadata insert failed (file is orphaned in storage):", error, path);
    throw error;
  }
  return fromDbDocument(data);
}

export async function getDocumentUrl(storagePath) {
  const client = supabase; // RLS enforces access — see supabase/migrations
  const { data, error } = await client.storage
    .from('documents')
    .createSignedUrl(storagePath, 3600); // 1-hour signed URL
  if (error) { console.error("Could not generate signed URL:", error, storagePath); throw error; }
  return data.signedUrl;
}

export async function deleteDocument(id, storagePath) {
  const client = supabase; // RLS enforces access — see supabase/migrations
  await client.storage.from('documents').remove([storagePath]);
  const { error } = await client.from('documents').delete().eq('id', id);
  if (error) throw error;
}

function fromDbDocument(d) {
  return {
    id:           d.id,
    name:         d.name,
    size:         d.size,
    type:         d.mime_type,
    storagePath:  d.storage_path,
    uploadedBy:   d.uploaded_by_name || 'Unknown',
    uploadedAt:   d.created_at,
    // dataUrl loaded on demand via getDocumentUrl()
    dataUrl:      null,
  };
}

// ─── AI DOCUMENT IMPORT (paid add-on) ──────────────────────────────────────────
// Short-lived staging area for the analyze-transaction-documents Edge
// Function to read from — it deletes these once analysis is done, success or
// failure. Own-user-only via Storage RLS (supabase/migrations/0007).

export async function uploadImportStagingFile(userId, importId, file) {
  const path = `${userId}/${importId}/${file.name}`;
  const { error } = await supabase.storage.from('import-staging').upload(path, file, { upsert: true });
  if (error) throw error;
  return path;
}

export async function analyzeTransactionDocuments({ stagingPaths, filenames, checklistTemplates }) {
  const data = await invokeEdgeFunction('analyze-transaction-documents', { stagingPaths, filenames, checklistTemplates });
  // documentImportUsed/Quota let the caller update local brokerage state
  // immediately instead of showing a stale remaining-imports count.
  return { ...data.result, _documentImportUsed: data.documentImportUsed, _documentImportQuota: data.documentImportQuota };
}

// ─── REALTIME ─────────────────────────────────────────────────────────────────

export function subscribeToTransactions(callback) {
  return supabase
    .channel('transactions')
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'transactions' },
      () => callback()
    )
    .subscribe();
}

export function unsubscribe(channel) {
  supabase.removeChannel(channel);
}

// ─── INVITE USER ──────────────────────────────────────────────────────────────
// Sending an Auth invite requires the service-role key, which never ships to
// the browser — this calls the invite-user Edge Function instead, which
// holds that key server-side. The function verifies the caller's own role
// from their JWT and stamps the new user's brokerage_id from the caller's
// own brokerage (never trusts a client-supplied brokerage_id).

// Shared caller for the invite-user/delete-user Edge Functions — both
// return HTTP 200 with an {error} body on handled failures (see those
// functions for why), with this non-2xx branch as a rare fallback for
// platform-gateway-level rejections (e.g. no auth header at all).
async function invokeEdgeFunction(name, body) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    try {
      const errBody = await error.context.json();
      throw new Error(errBody.error || error.message);
    } catch {
      throw error;
    }
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function inviteUser({ name, email, role, brokerId, licenseNo, phone }) {
  const data = await invokeEdgeFunction('invite-user', { name, email, role, brokerId, licenseNo, phone });
  return fromDbUser(data.profile);
}

export async function deleteUser(userId) {
  await invokeEdgeFunction('delete-user', { userId });
}

// Backs the platform-admin page (admin.txcoordinatorpro.com) — not part of
// the brokerage-tenant data model, see supabase/functions/platform-admin.
export async function callPlatformAdmin(action, payload = {}) {
  return invokeEdgeFunction('platform-admin', { action, ...payload });
}

export async function updateUserProfile(id, updates) {
  const { data, error } = await supabase
    .from('users')
    .update({
      name:       updates.name,
      role:       updates.role,
      broker_id:  updates.brokerId || null,
      license_no: updates.licenseNo || null,
      phone:      updates.phone || null,
    })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return fromDbUser(data);
}

// ─── CHECKLIST TEMPLATES ────────────────────────────────────────────────────
// RLS scopes these by the caller's own brokerage — no brokerage_id filter
// needed client-side. Only the principal broker can write (enforced by RLS);
// anyone in the brokerage can read, to build a new transaction's checklist.

function fromDbChecklistTemplate(d) {
  return {
    id:          d.id,
    templateId:  d.template_id,
    title:       d.title,
    mandatory:   d.mandatory,
    side:        d.side,
    category:    d.category,
    description: d.description || '',
    sortOrder:   d.sort_order,
    active:      d.active,
  };
}

export async function fetchChecklistTemplates() {
  const { data, error } = await supabase
    .from('checklist_templates')
    .select('*')
    .order('sort_order');
  if (error) throw error;
  return (data || []).map(fromDbChecklistTemplate);
}

export async function createChecklistTemplate(brokerageId, tpl) {
  const { data, error } = await supabase
    .from('checklist_templates')
    .insert({
      brokerage_id: brokerageId,
      template_id:  tpl.templateId || 'tpl_' + Date.now().toString(36),
      title:        tpl.title,
      mandatory:    !!tpl.mandatory,
      side:         tpl.side || 'Both',
      category:     tpl.category || 'Other',
      description:  tpl.description || '',
      sort_order:   tpl.sortOrder ?? 0,
      active:       tpl.active !== false,
    })
    .select()
    .single();
  if (error) throw error;
  return fromDbChecklistTemplate(data);
}

export async function updateChecklistTemplate(id, updates) {
  const d = {};
  if (updates.title       !== undefined) d.title       = updates.title;
  if (updates.mandatory   !== undefined) d.mandatory    = !!updates.mandatory;
  if (updates.side        !== undefined) d.side        = updates.side;
  if (updates.category    !== undefined) d.category    = updates.category;
  if (updates.description !== undefined) d.description = updates.description;
  if (updates.sortOrder   !== undefined) d.sort_order   = updates.sortOrder;
  if (updates.active      !== undefined) d.active      = !!updates.active;
  const { data, error } = await supabase
    .from('checklist_templates')
    .update(d)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return fromDbChecklistTemplate(data);
}

export async function deleteChecklistTemplate(id) {
  const { error } = await supabase.from('checklist_templates').delete().eq('id', id);
  if (error) throw error;
}

// ─── BROKERAGE BRANDING ─────────────────────────────────────────────────────
// RLS: anyone can read (needed pre-login for the login screen); only the
// principal broker can write, scoped to their own brokerage row.

function fromDbBrokerage(d) {
  if (!d) return null;
  return {
    id: d.id, name: d.name, slug: d.slug, logoUrl: d.logo_url, primaryColor: d.primary_color,
    maxUsers: d.max_users, maxTransactions: d.max_transactions,
    documentImportQuota: d.document_import_quota || 0, documentImportUsed: d.document_import_used || 0,
    active: d.active !== false,
  };
}

export async function fetchBrokerageById(id) {
  const { data, error } = await supabase.from('brokerages').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return fromDbBrokerage(data);
}

export async function updateBrokerage(id, updates) {
  const d = {};
  if (updates.name         !== undefined) d.name          = updates.name;
  if (updates.logoUrl      !== undefined) d.logo_url       = updates.logoUrl;
  if (updates.primaryColor !== undefined) d.primary_color  = updates.primaryColor;
  const { data, error } = await supabase.from('brokerages').update(d).eq('id', id).select().single();
  if (error) throw error;
  return fromDbBrokerage(data);
}

export async function uploadBrokerageLogo(brokerageId, file) {
  const ext  = file.name.split('.').pop();
  const path = `${brokerageId}/logo.${ext}`;
  const { error } = await supabase.storage
    .from('brokerage-assets')
    .upload(path, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from('brokerage-assets').getPublicUrl(path);
  // Cache-bust so a re-uploaded logo at the same path shows immediately
  return data.publicUrl + '?t=' + Date.now();
}

